import React from "react";

interface WalletStatsProps {
  stats: any;
  statsLoading: boolean;
  totalByDevise: Record<string, number>;
  totalRechargedByDevise: Record<string, number>;
  totalDebitedByDevise: Record<string, number>;
  // Nouveaux totaux journaliers - Recharges
  totalRechargedPendingByDevise: Record<string, number>;
  totalRechargedCompletedByDevise: Record<string, number>;
  totalRechargedCancelledByDevise: Record<string, number>;
  // Nouveaux totaux journaliers - Débits
  totalDebitedPendingByDevise: Record<string, number>;
  totalDebitedCompletedByDevise: Record<string, number>;
  totalDebitedCancelledByDevise: Record<string, number>;
}

export const WalletStats: React.FC<WalletStatsProps> = ({
  stats,
  statsLoading,
  totalByDevise,
  totalRechargedByDevise,
  totalDebitedByDevise,
  totalRechargedPendingByDevise,
  totalRechargedCompletedByDevise,
  totalRechargedCancelledByDevise,
  totalDebitedPendingByDevise,
  totalDebitedCompletedByDevise,
  totalDebitedCancelledByDevise,
}) => {
  const renderDeviseList = (data: Record<string, number>, colorClass: string = "text-gray-800") => {
    if (Object.keys(data).length === 0) {
      return <p className="text-gray-400">Aucun</p>;
    }
    return (
      <div className="space-y-1 mt-1">
        {Object.entries(data).map(([devise, total]) => (
          <p key={devise} className={`text-lg font-semibold ${colorClass}`}>
            {(total as number).toLocaleString()} <span className="text-sm text-gray-500">{devise}</span>
          </p>
        ))}
      </div>
    );
  };

  const renderLoading = () => <p className="text-lg text-gray-400">Chargement...</p>;

  return (
    <div className="space-y-6">
      {/* Ligne 1 : Statistiques générales */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500">Total Wallets</h3>
          {statsLoading ? (
            <p className="text-2xl font-bold text-gray-400">Chargement...</p>
          ) : (
            <p className="text-3xl font-bold text-green-600">{stats?.total_wallets || 0}</p>
          )}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500">Historique</h3>
          {statsLoading ? (
            <p className="text-2xl font-bold text-gray-400">Chargement...</p>
          ) : (
            <p className="text-3xl font-bold text-blue-600">{stats?.total_histories || 0}</p>
          )}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500">Soldes totaux</h3>
          {statsLoading ? renderLoading() : renderDeviseList(totalByDevise)}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500">Total rechargé</h3>
          {statsLoading ? renderLoading() : renderDeviseList(totalRechargedByDevise, "text-green-600")}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500">Total débité</h3>
          {statsLoading ? renderLoading() : renderDeviseList(totalDebitedByDevise, "text-red-600")}
        </div>
      </div>

      {/* Ligne 2 : Recharges journalières */}
      <div>
        <h2 className="text-lg font-semibold text-gray-700 mb-3">Recharges journalières</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500">En attente</h3>
            {statsLoading ? renderLoading() : renderDeviseList(totalRechargedPendingByDevise, "text-yellow-600")}
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500">Complété</h3>
            {statsLoading ? renderLoading() : renderDeviseList(totalRechargedCompletedByDevise, "text-green-600")}
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500">Annulé</h3>
            {statsLoading ? renderLoading() : renderDeviseList(totalRechargedCancelledByDevise, "text-red-600")}
          </div>
        </div>
      </div>

      {/* Ligne 3 : Débits journaliers */}
      <div>
        <h2 className="text-lg font-semibold text-gray-700 mb-3">Débits journaliers</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500">En attente</h3>
            {statsLoading ? renderLoading() : renderDeviseList(totalDebitedPendingByDevise, "text-yellow-600")}
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500">Complété</h3>
            {statsLoading ? renderLoading() : renderDeviseList(totalDebitedCompletedByDevise, "text-green-600")}
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500">Annulé</h3>
            {statsLoading ? renderLoading() : renderDeviseList(totalDebitedCancelledByDevise, "text-red-600")}
          </div>
        </div>
      </div>
    </div>
  );
};
